import { Button } from "@/components/ui/button";
import {
  EnrichedTask,
  useUpdateTask,
} from "@/hooks/admin/AdminTodosTabs/useTasks";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { Check } from "lucide-react";
import { useState } from "react";

interface TaskItemProps {
  task: EnrichedTask;
  onClick: () => void;
  onDragStart?: (task: EnrichedTask) => void;
}

const TaskItem = ({ task, onClick, onDragStart }: TaskItemProps) => {
  const updateTask = useUpdateTask();
  const [isDragging, setIsDragging] = useState(false);

  const handleToggleComplete = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newStatus = task.status === "completed" ? "pending" : "completed";
    const updates: any = {
      status: newStatus,
      completed_at: newStatus === "completed" ? new Date().toISOString() : null,
    };

    updateTask.mutate({ id: task.id, updates });
  };

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    e.dataTransfer.setData("application/json", JSON.stringify(task));
    e.dataTransfer.effectAllowed = "move";
    onDragStart?.(task);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const isCompleted = task.status === "completed";

  const getOverdueLabel = () => {
    if (!task.due_date) return null;
    const parsedDate = parseISO(task.due_date);
    return format(parsedDate, "d 'de' MMMM", { locale: es });
  };

  return (
    <div
      className={cn(
        "w-full p-3 border rounded-lg cursor-pointer transition-all duration-200 hover:bg-muted/50",
        isCompleted && "opacity-60 bg-muted/20",
        isDragging && "opacity-50 rotate-2 scale-95 shadow-lg",
      )}
      onClick={onClick}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {(task as any).isOverdue && task.due_date && (
            <span className="inline-block text-[10px] font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full mb-2">
              Vencida · {getOverdueLabel()}
            </span>
          )}

          <h4
            className={cn(
              "font-medium text-sm truncate mb-2",
              isCompleted && "line-through text-muted-foreground",
            )}
          >
            {task.title}
          </h4>

          {task.description && (
            <p className="text-xs text-muted-foreground mb-2">
              {task.description}
            </p>
          )}

          {task.assigned_user && (
            <p className="text-xs text-muted-foreground mt-1">
              {task.assigned_user.first_name} {task.assigned_user.last_name}
            </p>
          )}
        </div>

        <Button
          variant={isCompleted ? "default" : "outline"}
          size="sm"
          className={cn(
            "h-6 w-6 p-0 shrink-0",
            isCompleted && "bg-green-600 hover:bg-green-700",
          )}
          onClick={handleToggleComplete}
        >
          <Check className="h-3 w-3" />
        </Button>
      </div>

      {(task.property || task.property_id === "__external__" || task.tag) && (
        <div className="mt-2 pt-2 border-t">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {(task.property || task.property_id === "__external__") && (
              <span>
                {task.property_id === "__external__"
                  ? "Propiedad externa"
                  : task.property?.short_name || task.property?.title}
              </span>
            )}
            {task.tag && (
              <span style={{ color: task.tag.color }}>#{task.tag.name}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskItem;
